import wretch from 'wretch';
import sortBy from 'lodash/sortBy';

import {
  LoadDictionaryByTypeMethods,
  LoadDictionaryMethods,
  LoadDictionaryOptionsType,
  LoadTransactionSubTypesDictionaryByTypeMethods,
} from './types';
import {
  ILanguageModel,
  IBusinessUnitByEnvironmentModel,
  IEnvironmentModel,
  IBusinessUnitModel,
  ICallUrlTemplatesModel,
  ISmsUserDomainModel,
  ISmsShortGroupsModel,
  ISmsCenterAssetModel,
  ITimeZoneModel,
  ITradingStatusModel,
  IAvailableBrandModel,
  ILeadTagModel,
  ICountryStateModel,
  ICallProviderModel,
  ICallCenterTypeModel,
  ISmsProviderModel,
  ICountriesModel,
  ISmsProviderTypeModel,
  IChannelModel,
  IMailListUserDomainModel,
  IPaymentGroupModel,
  IAffiliateSystemsModel,
  ITrackingLinksModel,
  IVideoVerificationStatusModel,
  IUserDomainsModel,
  ITradingPlatformsModel,
  IManagerSignaturesByEnvironmentModel,
  dictionariesByType,
  IRoleModel,
  ISalesManagersByTeam,
  IVipManagersByTeam,
  IRetentionManagersByTeam,
  ICloserManagersByTeam,
  IOtherDepositTransactionSubTypeModel,
  TransactionTypes,
  IQuotesAssetsModel,
  IDividendStocksModel,
  ICardProcessingEnvironment,
  IDocumentTypeModel,
  ITeamsLocationModel,
  IManagerSignatureModel,
  IOrganizationUnitTypeModel,
  ISumAndSubstanceTypeModel,
  ITransactionSubtypesModel,
  ICurrenciesModel,
  IDocumentsRejectedReasons,
} from './models';
import {
  dictionaryIdMapper,
  dictionaryNameMapper,
  getSortedDictionary,
} from './mappers';

function* loadDictionary<T>(
  url: string,
  {
    keyForId,
    keyForName,
    order,
    customMapper,
  }: LoadDictionaryOptionsType<T> = {}
) {
  let dictionary: T[] = yield wretch(url).get().json();

  if (customMapper) dictionary = customMapper(dictionary);

  if (keyForName) dictionary = dictionaryNameMapper<T>(dictionary, keyForName);

  if (keyForId) dictionary = dictionaryIdMapper<T>(dictionary, keyForId);

  if (order) dictionary = getSortedDictionary<T>(dictionary, order);

  return dictionary;
}

export const loadDictionaryByTypeMethods = Object.entries(
  dictionariesByType
).reduce((dictionaries, [key, value]) => {
  dictionaries[key] = (options) =>
    loadDictionary(`/api/dictionaries/by-type/${value}`, options);

  return dictionaries;
}, {} as LoadDictionaryByTypeMethods);

export const loadTransactionSubTypesDictionaryByTypeMethods = Object.entries(
  TransactionTypes
).reduce((dictionaries, [key, value]) => {
  dictionaries[key] = (options) =>
    loadDictionary(
      `/api/transaction-subtypes/by-transaction-type/${value}`,
      options
    );

  return dictionaries;
}, {} as LoadTransactionSubTypesDictionaryByTypeMethods);

export const loadDictionaryMethods: LoadDictionaryMethods = {
  environment: () => loadDictionary<IEnvironmentModel>('/api/environments'),
  businessUnit: () => loadDictionary<IBusinessUnitModel>('/api/business-units'),
  leadTag: () => loadDictionary<ILeadTagModel>('/api/lead-tags'),
  tradingPlatforms: (options) =>
    loadDictionary<ITradingPlatformsModel>('/api/trading-platforms', options),
  availableTradingPlatforms: (options) =>
    loadDictionary<ITradingPlatformsModel>(
      `/api/trading-platforms/available?leadId=${options.leadId}`
    ),
  timeZone: () => loadDictionary<ITimeZoneModel>('/api/timezones'),
  language: () => loadDictionary<ILanguageModel>('/api/languages'),
  affiliateSystems: () =>
    loadDictionary<IAffiliateSystemsModel>('/api/affiliate-systems'),
  channels: () => loadDictionary<IChannelModel>('/api/channels'),
  countryStates: () =>
    loadDictionary<ICountryStateModel>('/api/country-states'),
  countries: () => loadDictionary<ICountriesModel>('/api/countries'),
  tradingStatus: () =>
    loadDictionary<ITradingStatusModel>('/api/dictionaries/trading-statuses'),
  paymentGroups: () =>
    loadDictionary<IPaymentGroupModel>('/api/dictionaries/payment-groups'),
  userDomains: () =>
    loadDictionary<IUserDomainsModel>('/api/dictionaries/user-domains'),
  businessUnitByEnvironment: (options) =>
    loadDictionary<IBusinessUnitByEnvironmentModel>(
      `/api/business-units/by-environment-id/${options.id}`
    ),
  quotesAssets: (options) =>
    loadDictionary<IQuotesAssetsModel>(
      `/api/quotes/assets?platformName=${options}`,
      {
        customMapper: (assets) =>
          assets.map(({ name, currency }) => ({
            id: `${name};${currency}` as any,
            currency,
            name: `${name} [${currency}]`,
          })),
      }
    ),
  dividendStocks: (options) =>
    loadDictionary<IDividendStocksModel>(
      `/api/dividends/stocks?symbol=${options.name}`,
      {
        customMapper: (assets) =>
          assets.map((asset) => ({
            ...asset,
            id: asset.stockId,
            prefix: asset.flag,
            name: `${asset.symbol}:${asset.exchangeName} - ${asset.companyName}` as any,
          })),
      }
    ),
  managerSignaturesByEnvironment: (options) =>
    loadDictionary<IManagerSignaturesByEnvironmentModel>(
      `/api/manager-signatures/by-environment-id/${options.id}`
    ),
  closerManager: () =>
    loadDictionary<ICloserManagersByTeam>('/api/managers/by-department/closer'),
  retentionManager: () =>
    loadDictionary<IRetentionManagersByTeam>(
      '/api/managers/by-department/retention'
    ),
  vipManager: () =>
    loadDictionary<IVipManagersByTeam>('/api/managers/by-department/vip'),
  salesManager: () =>
    loadDictionary<ISalesManagersByTeam>('/api/managers/by-department/sales'),
  // remove same dictionary ?
  conversionAgent: () =>
    loadDictionary<ISalesManagersByTeam>('/api/managers/by-department/sales'),
  availableBrands: (options) =>
    loadDictionary<IAvailableBrandModel>(
      `/api/leads/${options.leadId}/available-brands`,
      options
    ),
  callUrlTemplates: () =>
    loadDictionary<ICallUrlTemplatesModel>(
      '/api/callurltemplates/call-url-templates'
    ),
  callProviders: (options) =>
    loadDictionary<ICallProviderModel>(
      `/api/call-providers?leadId=${options.leadId}&phone=${options.phone}`
    ),
  trackingLinks: (options) =>
    loadDictionary<ITrackingLinksModel>('/api/trackinglinks/headers', options),
  videoVerificationStatuses: () =>
    loadDictionary<IVideoVerificationStatusModel>(
      '/api/dictionaries/video-verification-statuses'
    ),
  callCenterTypes: (options) =>
    loadDictionary<ICallCenterTypeModel>(
      '/api/call-url-templates/call-center-types',
      options
    ),
  mailListUserDomain: () =>
    loadDictionary<IMailListUserDomainModel>(
      '/api/mail-lists/1/dictionaries/domains'
    ),
  roles: () => loadDictionary<IRoleModel>('/api/roles/roles'),
  smsProviders: (options) =>
    loadDictionary<ISmsProviderModel>(
      `/api/sms-provider-accounts/by-environment/${options.environmentId}`
    ),
  leadSmsProviders: (options) =>
    loadDictionary<ISmsProviderModel>(
      `/api/sms-provider-accounts/?environmentId=${options.environmentId}&phoneCode=${options.phoneCode}&leadCountryId=${options.leadCountryId}`,
      {
        customMapper: (assets) =>
          assets.map((asset) => ({
            ...asset,
            name: `${asset.name} [${asset.smsProviderType}]`,
          })),
      }
    ),
  smsProviderTypes: (options) =>
    loadDictionary<ISmsProviderTypeModel>('/api/sms/provider-types', options),
  smsUserDomain: () =>
    loadDictionary<ISmsUserDomainModel>(
      '/api/sms-signals/1.0/dictionaries/domains'
    ),
  smsCenterAsset: (options) =>
    loadDictionary<ISmsCenterAssetModel>(
      '/api/sms-signals/1.0/assets',
      options
    ),
  smsShortGroups: () =>
    loadDictionary<ISmsShortGroupsModel>(
      '/api/sms-signals/1.0/groups/short-groups',
      {
        order: (dictionary) =>
          sortBy(dictionary, (dictionaryItem) => dictionaryItem.name),
      }
    ),
  otherDepositTransactionSubTypes: (options) =>
    loadDictionary<IOtherDepositTransactionSubTypeModel>(
      `/api/deposit-payment-methods/${options.leadId}`
    ),
  cardProcessingEnvironments: () =>
    loadDictionary<ICardProcessingEnvironment>(
      '/api/card-processing-environments'
    ),
  documentTypes: () =>
    loadDictionary<IDocumentTypeModel>('/api/document-types'),
  teamLocations: () =>
    loadDictionary<ITeamsLocationModel>('/api/team-locations'),
  managerSignatures: (options) =>
    loadDictionary<IManagerSignatureModel>(
      `/api/manager-signatures/by-environment-id/${options}`
    ),
  organizationUnitTypes: () =>
    loadDictionary<IOrganizationUnitTypeModel>('/api/organization-unit-types'),
  sumAndSubstanceTypes: () =>
    loadDictionary<ISumAndSubstanceTypeModel>(
      '/api/dictionaries/sum-and-substance-types'
    ),
  transactionSubtypes: () =>
    loadDictionary<ITransactionSubtypesModel>('/api/transaction-subtypes'),
  supportedTransactionTypes: () =>
    loadDictionary<ITransactionSubtypesModel>(
      '/api/transaction-subtypes/supported-transaction-types'
    ),
  currencies: () => loadDictionary<ICurrenciesModel[]>('/api/currencies'),

  balanceTransactionTypes: () =>
    Promise.resolve([
      { id: 1, name: 'Real' },
      { id: 0, name: 'Credit' },
    ]),

  documentsRejectedReasons: () =>
    loadDictionary<IDocumentsRejectedReasons>(
      '/api/dictionaries/document-reject-reasons'
    ),
};
